import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { LoopInput } from "./Loop.ts";
import { LoopDocument } from "./LoopDocument.ts";

/**
 * LoopVersion model — one FROZEN edition of a living {@link Loop} (OL-113).
 *
 * The loop is living; each of its versions is immutable. Every content change
 * to the head appends one of these carrying the content as patched, so a
 * report, a regression or a cost spike is attributable to exactly the
 * definition that produced it — input schema and defaults included, which the
 * rendered statement alone does not preserve.
 *
 * There is deliberately **no `updatedAt`**: the row is append-only, so there is
 * nothing to stamp. There is deliberately **no `customerId`**: a version has no
 * tenancy of its own and is reachable only through its loop, which carries it.
 *
 * The nine content fields mirror {@link Loop}'s nine content fields exactly.
 * `name` / `description` are absent because they are the head's identity, and a
 * rename is not a new edition.
 *
 * @since 1.0.0
 */
export class LoopVersion extends Model.Class<LoopVersion>("LoopVersion")({
  id: Model.Generated(Schema.String),

  /** The head this version belongs to. No foreign key — a cascade would delete evidence. */
  loopId: Schema.String,
  /** 1..n, unique within the loop. Stamped onto `Outcome.loopVersion` by every invocation. */
  version: Schema.Number,

  /** Frozen content: the outcome statement with named inputs still to interpolate. */
  statementTemplate: Schema.String,
  /** Frozen content: acceptance stated apart from the statement, or `null`. */
  acceptanceTemplate: Schema.NullOr(Schema.String),
  /** Frozen content: the named inputs the templates interpolate. */
  inputSchema: Schema.Array(LoopInput),
  /** Frozen content: the budget ceiling an invocation of this version inherits. */
  defaultBudgetUsd: Schema.NullOr(Schema.Number),
  /** Frozen content: the concurrent-run governor. */
  defaultMaxParallel: Schema.NullOr(Schema.Number),
  /** Frozen content: the orchestrator-turn ceiling. */
  defaultMaxTurns: Schema.NullOr(Schema.Number),
  /** Frozen content: the repo. */
  defaultRepo: Schema.NullOr(Schema.String),
  /** Frozen content: the git ref. */
  defaultRef: Schema.NullOr(Schema.String),
  // Field order mirrors the head's content block, INDEPENDENT of the physical
  // column order (only `src/tables/loop-versions.ts` declares `body` last). Do
  // not "fix" this to match the table.
  /** Frozen content: the v3 loop document, or `null` for a template-only definition. */
  body: Schema.NullOr(LoopDocument),

  /** The owner's one line on why this version exists. */
  changeNote: Schema.NullOr(Schema.String),
  /** Who appended it. No foreign key — cross-schema by design. */
  createdByPersonId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
}) {}
